import Info from "@assets/icons/info.svg";

import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager } from "@core/service-container";
import { Exercise } from "@domain/exercise/exercise";
import { sendAnalyticsEventAddExercise } from "@domain/library/exercise-analytics";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog } from "@ui/components/dialog/base-dialog";
import { NetworkError } from "@ui/components/common/error";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { Tooltip } from "@ui/components/common/tooltip";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { useExerciseLibrary } from "@ui/pages/program-builder/hooks/use-exercise-library";
import { ExerciseLibrarySearchFilter } from "@ui/pages/program-builder/library/sidebar/exercise-library-search-filter";
import { ExerciseLibraryCounts } from "@ui/pages/program-builder/library/sidebar/exercise-library-counts";
import { MobileLibraryExerciseCard } from "@ui/pages/program-builder/mobile/mobile-library-exercise-card";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { useObservable } from "micro-observables";
import { DialogProps } from "primereact/dialog";
import { WordingKey } from "@typings/i18n-typings";
import React, { useCallback, useContext, useState } from "react";

export enum ExerciseLibraryFlow {
	Default = "Default",
	ComplementaryExercises = "ComplementaryExercises",
	AlternativeExercises = "AlternativeExercises",
}

export interface MobileExerciseLibraryDialogProps extends DialogProps {
	exerciseLibraryAddingFlow: ExerciseLibraryFlow;
	onClose: (exercises: Exercise[]) => void;
}

const getAddButtonWordingKey = (exerciseLibraryAddingFlow: ExerciseLibraryFlow): WordingKey => {
	if (exerciseLibraryAddingFlow === ExerciseLibraryFlow.ComplementaryExercises) {
		return "programBuilder.mobileExerciseLibraryDialog.addButton.complementaryExercises";
	}

	if (exerciseLibraryAddingFlow === ExerciseLibraryFlow.AlternativeExercises) {
		return "programBuilder.mobileExerciseLibraryDialog.addButton.alternativeExercises";
	}

	return "programBuilder.mobileExerciseLibraryDialog.addButton";
};

export const MobileExerciseLibraryDialog = ({
	exerciseLibraryAddingFlow,
	onClose,
	...otherProps
}: MobileExerciseLibraryDialogProps): JSX.Element => {
	const { format } = useI18n();

	const selectedExercisesOnAlternativeSelection = useObservable(libraryFilterManager.exerciseWithAlternativesEditedList);
	const {
		filteredSelectedExercises: filteredPreSelectedExercises,
		filteredExercises,
		isAnyFilterActive,
		hasSelectedExercises: hasPreSelectedExercises,
		refreshLibraryExercises,
		openExerciseDetailsModal,
		isError,
		isLoading,
	} = useExerciseLibrary();
	const trackingContext = useContext(TrackingContext);
	const [selectedExercises, setSelectedExercises] = useState<Exercise[]>([]);

	const toggleExerciseSelected = useCallback((exercise: Exercise) => {
		setSelectedExercises(current => {
			if (current.includes(exercise)) {
				return current.filter(e => e.id !== exercise.id);
			} else {
				return [...current, exercise];
			}
		});
	}, []);

	const renderSelectableMobileLibraryExerciseCard = (exercise: Exercise) => (
		<MobileLibraryExerciseCard
			key={exercise.id}
			exercise={exercise}
			selected={selectedExercises.includes(exercise)}
			onSelect={() => toggleExerciseSelected(exercise)}
			onThumbnailClick={() => openExerciseDetailsModal(exercise)}
		/>
	);

	const addButtonWordingKey = getAddButtonWordingKey(exerciseLibraryAddingFlow);

	let content: JSX.Element;
	if (isLoading) {
		content = <StyledLoadingContainer />;
	} else if (isError) {
		content = (
			<StyledNetworkError
				withoutIcon
				description={format("exerciseLibrary.errorMessage")}
				onRefresh={refreshLibraryExercises}
			/>
		);
	} else {
		content = (
			<>
				<ExerciseLibrarySearchFilter />
				{!isAnyFilterActive && !hasPreSelectedExercises && <StyledExerciseLibraryCounts mode="LibraryAndAdded" />}
				<ScrollContainer>
					{filteredPreSelectedExercises.length > 0 && (
						<>
							<Section className="p-size-m-bold">{format("exerciseLibrary.selectedExercisesSection")}</Section>
							<StyledExerciseLibraryCounts mode="PreSelected" />
							<List>{filteredPreSelectedExercises.map(exercise => renderSelectableMobileLibraryExerciseCard(exercise))}</List>
						</>
					)}
					{filteredExercises.length > 0 && (
						<>
							{hasPreSelectedExercises && (
								<>
									<Section className="p-size-m-bold">{format("exerciseLibrary.librarySection")}</Section>
									<StyledExerciseLibraryCounts mode="Library" />
								</>
							)}
							<List>{filteredExercises.map(exercise => renderSelectableMobileLibraryExerciseCard(exercise))}</List>
						</>
					)}
					{selectedExercisesOnAlternativeSelection.length > 0 && (
						<>
							<div>
								<Sort>
									<div className="p-size-m-bold">{format("exerciseLibrary.alreadyAdded")}</div>
									<StyledTooltip
										content={format("exerciseLibrary.alreadyInSelectionToolTipMessage", {
											exerciseSelectedCount: selectedExercisesOnAlternativeSelection.length,
										})}
										maxWidth={300}
									>
										<IconContainer>
											<Info width={22} height={22} color={colors.grey80} />
										</IconContainer>
									</StyledTooltip>
								</Sort>
								{hasPreSelectedExercises && <StyledExerciseLibraryCounts mode="Added" />}
							</div>
							<List>
								{selectedExercisesOnAlternativeSelection.map(exercise => (
									<MobileLibraryExerciseCard
										key={exercise.id}
										exercise={exercise}
										onSelect={() => undefined}
										onThumbnailClick={() => undefined}
										disabled
									/>
								))}
							</List>
						</>
					)}
				</ScrollContainer>
			</>
		);
	}

	return (
		<StyledDialog
			{...otherProps}
			header={format("exerciseLibrary.header")}
			footer={
				<AddButton
					disabled={selectedExercises.length === 0}
					onClick={() => {
						selectedExercises.forEach(exercise => sendAnalyticsEventAddExercise(exercise, trackingContext.category));
						onClose(selectedExercises);
						otherProps.onHide();
					}}
				>
					{format(addButtonWordingKey, { count: selectedExercises.length })}
				</AddButton>
			}
		>
			<Container>{content}</Container>
		</StyledDialog>
	);
};

const StyledDialog = styled(BaseDialog)`
	.p-dialog-content {
		padding-right: 0;
	}
`;
const AddButton = styled(Button)`
	width: 100% !important;
`;

const StyledNetworkError = styled(NetworkError)`
	place-content: center;
	flex: 1;
	padding: 32px;
`;

const StyledLoadingContainer = styled(LoadingContainer)`
	place-content: center;
	flex: 1;
`;

const Container = styled.div`
	${stack(8, "flexstart", "stretch")};
	flex: 1;
	min-height: 0px;
`;

const Section = styled.div`
	margin: 12px 0 0;
`;

const StyledExerciseLibraryCounts = styled(ExerciseLibraryCounts)`
	margin-bottom: 8px;
`;

const List = styled.div`
	${stack(12, "flexstart", "stretch")};
	padding-top: 4px;
	padding-right: 16px;
`;

const ScrollContainer = styled.div`
	flex: 1 1 auto;
	overflow-y: auto;
	min-height: 0px;
`;

const Sort = styled.div`
	${row(16)}
	margin: 12px 0 0;
	color: ${colors.grey};
`;

const StyledTooltip = styled(Tooltip)`
	font-size: 16px;
`;

const IconContainer = styled.div`
	display: flex;
`;
